<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="75%">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy"
                @submit.prevent="storeExpense">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>New Expense</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="6">
                            <v-text-field v-model="formData['label']" :rules="[vRules.required]"
                                          autofocus density="compact" label="Expense Label"/>
                        </v-col>
                        <v-col class="py-0" cols="6">
                            <v-select v-model="formData['driver_id']" :items="drivers" :rules="[vRules.required]"
                                      density="compact" :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()" item-value="id"
                                      label="Assigned Driver"/>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="py-0 my-3" cols="12">
                            <h4>Expense List</h4>
                            <v-table>
                                <thead>
                                <tr>
                                    <th class="font-weight-bold">Expense Type</th>
                                    <th class="font-weight-bold">Notes</th>
                                    <th class="font-weight-bold">Qty</th>
                                    <th class="font-weight-bold">Unit Cost</th>
                                    <th class="font-weight-bold">Tax</th>
                                    <th class="font-weight-bold">Total</th>
                                    <th style="width: 15px">
                                        <v-btn color="black" icon="mdi-plus" size="x-small" variant="flat"
                                               @click="addItem"/>
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(item,index) in formData['items']" :key="index">
                                    <td class="pa-0">
                                        <v-select v-model="item['vehicle_expense_type_id']" :items="expenseTypes"
                                                  :rules="[vRules.required]" density="compact" hide-details
                                                  item-title="name" item-value="id" label="Expense Type"/>
                                    </td>
                                    <td class="pa-0">
                                        <v-text-field v-model="item['notes']" density="compact" hide-details
                                                      label="Notes"/>
                                    </td>
                                    <td class="pa-0">
                                        <v-text-field v-model="item['qty']" :rules="[vRules.required]" density="compact"
                                                      hide-details label="Qty" min="1" type="number"/>
                                    </td>
                                    <td class="pa-0">
                                        <v-text-field v-model="item['unit_cost']" :rules="[vRules.required]" density="compact"
                                                      hide-details label="Unit Cost" min="0.1" type="number"/>
                                    </td>
                                    <td class="pa-0">
                                        <v-text-field v-model="item['tax']" :rules="[vRules.required]" density="compact"
                                                      hide-details label="Tax" min="0.1" type="number"/>
                                    </td>
                                    <td class="pa-0 text-center font-weight-bold">{{
                                            ((parseInt(item['qty'] ?? 1) * parseFloat(item['unit_cost'] ?? 0)) + parseFloat(item['tax'] ?? 0)).toFixed(2)
                                        }}
                                    </td>
                                    <td class="pa-0 text-center">
                                        <v-btn v-if="index" class="mb-3" color="red" icon="mdi-close"
                                               size="small" variant="text" @click="removeItem(index)"/>
                                    </td>
                                </tr>

                                </tbody>
                            </v-table>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <h4>Payment Details</h4>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-select v-model="formData['driver_payment_type_id']" :items="driverPaymentTypes"
                                      :rules="[vRules.required]" density="compact" hide-details item-title="name"
                                      item-value="id" label="Payment Method"/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="formData['receipt_id']" :rules="[vRules.required]" density="compact"
                                          hide-details label="Receipt ID"/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="totals['cost']" density="compact" hide-details label="Total Cost"
                                          readonly type="number"/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="totals['tax']" density="compact" hide-details label="Total Tax"
                                          readonly type="number"/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field :model-value="totals['total_cost']" density="compact" hide-details
                                          label="Grand Total" readonly/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" :loading="processingForm" color="primary" size="small"
                           type="submit">Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatLocalDateTime} from "@/composables/useTimezone";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    driverPaymentTypes: Array,
    expenseTypes: Array,
    drivers: Array,
    asset: Object,
});
const formData = reactive({
    valid: false,
    label: `Expense Created on ${formatLocalDateTime(new Date())} for ${props.asset.label}`,
    asset_id: props.asset.id,
    driver_id: props.asset.driver_id ?? null,
    items: [
        {
            vehicle_expense_type_id: null,
            unit_cost: null,
            qty: null,
            tax: null,
            notes: null,
        }
    ]
});

const emits = defineEmits(['update:modelValue', 'addExpense']);
const dialogSwitch = ref(props.modelValue);
const processingForm = ref(false);
const errors = ref([]);

const totals = computed(() => {
    const total = {
        cost: 0,
        tax: 0,
        total_cost: 0,
    }
    formData['items'].map((item) => {
        total.cost += item.unit_cost ? (parseFloat(item.unit_cost) * parseInt(item.qty)) : 0;
        total.tax += item.tax ? parseFloat(item.tax) : 0;
    })
    total.cost = parseFloat(total.cost).toFixed(2);
    total.tax = parseFloat(total.tax).toFixed(2);

    total.total_cost = (parseFloat(total.cost) + parseFloat(total.tax)).toFixed(2);
    return total;
})
const addItem = () => {
    formData['items'].push({
        vehicle_expense_type_id: null,
        unit_cost: null,
        qty: null,
        tax: null,
        notes: null,
    })
}
const removeItem = (index) => {
    formData['items'].splice(index, 1)
}
const storeExpense = () => {
    errors.value = [];
    processingForm.value = true;
    send('post', `fleet/expenses`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addExpense', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
        .finally(() => processingForm.value = false);
}
</script>
